/**
 * Research Task Tracker
 *
 * Dynamic task tracking component for deep research processes
 *
 * Primary responsibilities:
 * - Track research tasks in real-time
 * - Show progress with loaders and checkmarks
 * - Display collapsible results for each task
 * - Beautiful link formatting with favicons
 * - Dynamic task addition without predicting end
 *
 * Dependencies:
 * - lucide-react for icons
 * - next/image for favicon optimization
 */

'use client'

import React, { useState, useEffect } from 'react'
import {
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Loader2,
  ExternalLink,
  Search,
  Brain,
  FileText,
  Target
} from 'lucide-react'
import { getGoogleFaviconUrl } from '@/lib/favicon'

// --------------

interface TaskStep {
  id: string
  name: string
  status: 'pending' | 'running' | 'completed' | 'error'
  message?: string
  progress?: number
  results?: TaskResult[]
  startTime?: number
  endTime?: number
}

// --------------

interface TaskResult {
  title: string
  url: string
  snippet?: string
  favicon?: string
}

// --------------

interface ResearchTaskTrackerProps {
  tasks: TaskStep[]
  className?: string
}

// --------------

const getTaskIcon = (taskName: string) => {
  if (taskName.toLowerCase().includes('discovery')) return Search
  if (taskName.toLowerCase().includes('analysis') || taskName.toLowerCase().includes('strategy'))
    return Brain
  if (taskName.toLowerCase().includes('report') || taskName.toLowerCase().includes('synthesis'))
    return FileText
  if (taskName.toLowerCase().includes('dimension') || taskName.toLowerCase().includes('query'))
    return Target
  return Search
}

// --------------

const getFaviconUrl = (url: string): string => {
  return getGoogleFaviconUrl(url, 16) ?? '/favicon.ico'
}

// --------------

const formatDuration = (startTime?: number, endTime?: number): string => {
  if (!startTime) return ''
  const end = endTime || Date.now()
  const duration = Math.round((end - startTime) / 1000)
  return duration < 60 ? `${duration}s` : `${Math.floor(duration / 60)}m ${duration % 60}s`
}

// --------------

const TaskResultItem: React.FC<{ result: TaskResult }> = ({ result }) => {
  const [imageError, setImageError] = useState(false)

  return (
    <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800">
      <div className="mt-1 shrink-0">
        {!imageError ? (
          <img
            src={getFaviconUrl(result.url)}
            alt=""
            className="size-4"
            onError={() => setImageError(true)}
          />
        ) : (
          <ExternalLink className="size-4 text-gray-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <a href={result.url} target="_blank" rel="noopener noreferrer" className="group block">
          <div className="line-clamp-2 text-sm font-medium text-gray-900 transition-colors group-hover:text-blue-600 dark:text-gray-100 dark:group-hover:text-blue-400">
            {result.title}
          </div>
          {result.snippet && (
            <div className="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">
              {result.snippet}
            </div>
          )}
          <div className="mt-1 truncate text-xs text-gray-500 dark:text-gray-500">
            {new URL(result.url).hostname}
          </div>
        </a>
      </div>
    </div>
  )
}

// --------------

const TaskItem: React.FC<{ task: TaskStep }> = ({ task }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const TaskIcon = getTaskIcon(task.name)

  const getStatusIcon = () => {
    switch (task.status) {
      case 'completed':
        return <CheckCircle className="size-5 text-green-500" />
      case 'running':
        return <Loader2 className="size-5 animate-spin text-blue-500" />
      case 'error':
        return (
          <div className="flex size-5 items-center justify-center rounded-full bg-red-500">
            <div className="size-2 rounded-full bg-white" />
          </div>
        )
      default:
        return <div className="size-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />
    }
  }

  const hasResults = task.results && task.results.length > 0

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <div
        className={`cursor-pointer p-4 transition-colors ${
          hasResults ? 'hover:bg-gray-50 dark:hover:bg-gray-800/30' : ''
        }`}
        onClick={() => hasResults && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {getStatusIcon()}
          <TaskIcon className="size-4 text-gray-500 dark:text-gray-400" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {task.name}
              </span>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                {task.status === 'completed' && task.results && (
                  <span>{task.results.length} sources</span>
                )}
                <span>{formatDuration(task.startTime, task.endTime)}</span>
                {hasResults &&
                  (isExpanded ? (
                    <ChevronDown className="size-4" />
                  ) : (
                    <ChevronRight className="size-4" />
                  ))}
              </div>
            </div>
            {task.message && (
              <div className="mt-1 truncate text-xs text-gray-600 dark:text-gray-400">
                {task.message}
              </div>
            )}
            {task.status === 'running' && task.progress !== undefined && (
              <div className="mt-2">
                <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className="h-1.5 rounded-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${task.progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isExpanded && hasResults && (
        <div className="border-t border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-800/20">
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {task.results!.map((result, index) => (
              <TaskResultItem key={index} result={result} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// --------------

export const ResearchTaskTracker: React.FC<ResearchTaskTrackerProps> = ({
  tasks,
  className = ''
}) => {
  const completedTasks = tasks.filter((t) => t.status === 'completed').length
  const totalTasks = tasks.length
  const runningTask = tasks.find((t) => t.status === 'running')

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900 ${className}`}
    >
      <div className="border-b border-gray-200 p-4 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Research Progress</h3>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {completedTasks}/{totalTasks} completed
          </div>
        </div>
        {runningTask && (
          <div className="mt-2 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
            <Loader2 className="size-4 animate-spin" />
            {runningTask.message || runningTask.name}
          </div>
        )}
      </div>

      <div className="max-h-96 space-y-3 overflow-y-auto p-4">
        {tasks.map((task) => (
          <TaskItem key={task.id} task={task} />
        ))}

        {tasks.length === 0 && (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            <Search className="mx-auto mb-2 size-8 opacity-50" />
            <div className="text-sm">No research tasks yet</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ResearchTaskTracker
